// src/pages/Admin/VotePage/components/VoteQuestionsModal.jsx

import React from "react";

import {
  FaEdit,
  FaListUl,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../../components/Modal/Modal";

import { QUESTION_TYPE_OPTIONS } from "../data/voteAdminOptions";
import useVoteQuestions from "../hooks/useVoteQuestions";

const QUESTION_TYPE_STYLES = {
  single:
    "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20",

  multiple:
    "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",

  open:
    "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
};

const getQuestionTypeLabel = (type) =>
  QUESTION_TYPE_OPTIONS.find(
    (option) => option.value === type,
  )?.label || "Réponse libre";

export default function VoteQuestionsModal({
  isOpen,
  onClose,
  voteId,
  voteTitle,
}) {
  const {
    questions,
    questionForm,
    editingId,
    choiceDrafts,
    loading,
    savingQuestion,
    processingChoiceId,
    handleQuestionFieldChange,
    startEditing,
    cancelEditing,
    saveQuestion,
    removeQuestion,
    setChoiceDraft,
    addChoice,
    removeChoice,
  } = useVoteQuestions({
    isOpen,
    voteId,
  });

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      labelledById="vote-questions-title"
      describedById="vote-questions-description"
      initialFocusSelector="#question-textarea"
    >
      <ModalHeader
        id="vote-questions-title"
        title={`Questions — ${
          voteTitle || `Vote #${voteId}`
        }`}
        subtitle="Créez les questions et configurez les réponses proposées."
        onClose={onClose}
      />

      <ModalBody id="vote-questions-description">
        <form
          onSubmit={saveQuestion}
          className="mb-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-800/40"
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 className="font-black text-slate-900 dark:text-white">
                {editingId
                  ? "Modifier la question"
                  : "Ajouter une question"}
              </h3>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                Le contenu HTML reste accepté dans le texte.
              </p>
            </div>

            {editingId && (
              <button
                type="button"
                onClick={cancelEditing}
                className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-black text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              >
                <FaTimes />
                Annuler
              </button>
            )}
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
            <textarea
              id="question-textarea"
              name="question_text"
              value={questionForm.question_text}
              onChange={handleQuestionFieldChange}
              rows={4}
              placeholder="Saisissez le texte de la question…"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />

            <div>
              <label className="text-xs font-black uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Type de réponse
              </label>

              <select
                name="type"
                value={questionForm.type}
                onChange={handleQuestionFieldChange}
                className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              >
                {QUESTION_TYPE_OPTIONS.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                disabled={savingQuestion}
                className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 text-sm font-black text-white transition hover:bg-indigo-700 disabled:opacity-50"
              >
                <FaSave />

                {savingQuestion
                  ? "Enregistrement…"
                  : editingId
                    ? "Modifier"
                    : "Ajouter"}
              </button>
            </div>
          </div>
        </form>

        {loading ? (
          <div className="space-y-3">
            {[1, 2].map((item) => (
              <div
                key={item}
                className="h-48 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((question, questionIndex) => (
              <article
                key={question.id}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
              >
                <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-800/40 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[10px] font-black text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        Question {questionIndex + 1}
                      </span>

                      <span
                        className={`rounded-full px-2.5 py-1 text-[10px] font-black ring-1 ${
                          QUESTION_TYPE_STYLES[
                            question.type
                          ] ||
                          QUESTION_TYPE_STYLES.open
                        }`}
                      >
                        {getQuestionTypeLabel(
                          question.type,
                        )}
                      </span>
                    </div>

                    <div
                      className="text-sm font-black leading-relaxed text-slate-900 dark:text-white"
                      dangerouslySetInnerHTML={{
                        __html:
                          question.question_text || "",
                      }}
                    />
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        startEditing(question)
                      }
                      className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-black text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    >
                      <FaEdit />
                      Modifier
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        removeQuestion(question)
                      }
                      className="inline-flex h-9 items-center gap-2 rounded-xl bg-rose-50 px-3 text-xs font-black text-rose-700 ring-1 ring-rose-100 hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                    >
                      <FaTrash />
                      Supprimer
                    </button>
                  </div>
                </div>

                {(question.type === "single" ||
                  question.type === "multiple") && (
                  <div className="p-4">
                    <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-slate-400">
                      <FaListUl />
                      Réponses proposées
                    </div>

                    <div className="space-y-2">
                      {question.choices?.map(
                        (choice, choiceIndex) => (
                          <div
                            key={choice.id}
                            className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50"
                          >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-xs font-black text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
                              {choiceIndex + 1}
                            </span>

                            <span className="min-w-0 flex-1 text-sm font-bold text-slate-700 dark:text-slate-200">
                              {choice.choice_text}
                            </span>

                            <button
                              type="button"
                              disabled={
                                processingChoiceId ===
                                choice.id
                              }
                              onClick={() =>
                                removeChoice(choice)
                              }
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-rose-600 transition hover:bg-rose-50 disabled:opacity-50 dark:text-rose-300 dark:hover:bg-rose-500/10"
                              title="Supprimer cette proposition"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ),
                      )}

                      {!question.choices?.length && (
                        <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-xs italic text-slate-500 dark:border-slate-700 dark:text-slate-400">
                          Aucune proposition enregistrée.
                        </div>
                      )}
                    </div>

                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        value={
                          choiceDrafts[question.id] || ""
                        }
                        onChange={(event) =>
                          setChoiceDraft(
                            question.id,
                            event.target.value,
                          )
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            addChoice(question.id);
                          }
                        }}
                        placeholder="Ajouter une nouvelle proposition…"
                        className="h-10 min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      />

                      <button
                        type="button"
                        disabled={
                          processingChoiceId === question.id
                        }
                        onClick={() =>
                          addChoice(question.id)
                        }
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-xs font-black text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        <FaPlus />
                        Ajouter
                      </button>
                    </div>
                  </div>
                )}

                {question.type === "open" && (
                  <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
                    Cette question accepte une réponse textuelle
                    libre. Aucune proposition n’est nécessaire.
                  </div>
                )}
              </article>
            ))}

            {!questions.length && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-900">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  <FaListUl />
                </div>

                <div className="mt-3 font-black text-slate-700 dark:text-slate-200">
                  Aucune question
                </div>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Utilisez le formulaire ci-dessus pour créer
                  la première question.
                </p>
              </div>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-100 px-5 text-sm font-black text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700"
        >
          Fermer
        </button>
      </ModalFooter>
    </Modal>
  );
}